/**
 * Karr's windows: the wire between Karr and each copy of Karr floating over it.
 *
 * Founder, 26 Sep 2026, on the audit adjustment form: see the Profit & Loss and
 * the Balance Sheet while posting. Then: the button "duplicates the current
 * screen", floating on top, with the whole sidebar, as many times as works.
 *
 * Each window is a second Karr in an iframe, so ANY page works in it, today
 * and for every page built later, and each lays itself out for the window's
 * width. An iframe is recognised by its window NAME (`karr-sideview:<id>`),
 * which survives every navigation and reload inside the frame, so nothing has
 * to be carried in the address.
 *
 * Everything that crosses the boundary goes through `postToSide`/`readSide`,
 * and both ends check the origin and the sending window.
 *
 * @since 26 Sep 2026 - sideview (windows: sidewin)
 */

/** Every window's iframe `name` starts with this. Only the window manager sets it. */
export const SIDE_VIEW_NAME = 'karr-sideview';

/** True inside one of Karr's floating windows, false in the Karr around them. */
export function isSideView(): boolean {
    try {
        return typeof window !== 'undefined' && window.name.startsWith(SIDE_VIEW_NAME) && window.parent !== window;
    } catch {
        // A cross-origin parent throws on access: that is not one of ours.
        return false;
    }
}

/** Karr → a window. */
export type ToSide =
    | { kind: 'navigate'; href: string }
    | { kind: 'refresh' }
    | { kind: 'org'; orgId: string }
    /** The window's title-bar menu button: slide the sidebar out, or back. */
    | { kind: 'menu' };

/** Inside a window, the title-bar menu button arrives as this window event (WindowNav). */
export const WINDOW_MENU_EVENT = 'karr:window-menu';

/** A window → Karr. */
export type FromSide =
    | { kind: 'ready' }
    | { kind: 'location'; href: string; title: string | null; orgId: string | null }
    | { kind: 'key'; key: 'new-window' };

const TAG = '__karrSideView';

export function postToSide(target: Window | null | undefined, msg: ToSide | FromSide): void {
    if (!target) return;
    try {
        target.postMessage({ [TAG]: msg }, window.location.origin);
    } catch {
        // A frame that is mid-navigation can refuse a message; the next one lands.
    }
}

/** The message, if this event is one of ours from our own origin; else null. */
export function readSide<T extends ToSide | FromSide>(e: MessageEvent): T | null {
    if (e.origin !== window.location.origin) return null;
    const d = e.data as Record<string, unknown> | null;
    if (!d || typeof d !== 'object' || !(TAG in d)) return null;
    const m = d[TAG] as { kind?: unknown } | null;
    return m && typeof m.kind === 'string' ? (m as T) : null;
}

/** Ctrl+\ (⌘\ on a Mac) opens another window. */
export function isSideViewShortcut(e: KeyboardEvent): boolean {
    return (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key === '\\';
}
